import { MessageDescriptor, useIntl } from 'react-intl';

import { Button } from '@calm-web/design-system';

import Image from '@/components/image/NextImage';
import { useAnalytics } from '@/hooks/analytics';
import { useUserEligibility } from '@/hooks/user/useUserEligibility';
import { useRouterPush } from '@/hooks/utils/useRouterPush';

import messages from './messages';
import { Body, Copy, Heading, ImageContainer, Wrapper } from './styles';

export type SubpageUnlockProps = {
	unlockHeading: MessageDescriptor;
	unlockBody: MessageDescriptor;
	unlockBgImage: string;
};

const Unlock = ({ unlockHeading, unlockBody, unlockBgImage }: SubpageUnlockProps) => {
	const { isLoggedInEligibleToSubscribe, isLoggedInValid } = useUserEligibility();
	const { logEvent } = useAnalytics();
	const { formatMessage } = useIntl();
	const routerPush = useRouterPush();

	const getButtonProperties = (): [
		href: { pathname: string; queryParams?: Record<string, string | null | undefined> },
		label: string,
	] => {
		if (isLoggedInValid) {
			return [{ pathname: '/app' }, formatMessage(messages.ctaActiveSub)];
		}

		if (isLoggedInEligibleToSubscribe) {
			return [{ pathname: '/subscribe' }, formatMessage(messages.ctaLoggedInIneligible)];
		}

		return [{ pathname: '/signup-flow', queryParams: { source: 'homepage' } }, formatMessage(messages.cta)];
	};

	const [{ pathname, queryParams }, label] = getButtonProperties();

	const onClick = async () => {
		logEvent({
			eventName: 'Subpage : CTA : Clicked',
			eventProps: {
				section: 'unlock',
			},
		});

		await routerPush(pathname, queryParams);
	};

	return (
		<Wrapper>
			<Copy>
				<Heading>{formatMessage(unlockHeading)}</Heading>
				<Body>{formatMessage(unlockBody)}</Body>
				<Button fullWidth maxWidth="354px" backgroundColor="white" textColor="blackAlpha80" onPress={onClick}>
					{label}
				</Button>
			</Copy>
			<ImageContainer>
				<Image fill loading="lazy" src={unlockBgImage} alt="" style={{ objectFit: 'cover' }} />
			</ImageContainer>
		</Wrapper>
	);
};

export default Unlock;
